import { useBlocker, useRouter } from "@tanstack/solid-router";
import { onCleanup } from "solid-js";

/**
 * Back-button-closes-overlay (premium PWA/mobile pattern).
 *
 * When an overlay (dialog/sheet/command-palette/unrouted drawer) is open
 * and the user presses the browser's "back" button, we want to CLOSE the
 * overlay instead of navigating through history.
 *
 * IMPORTANT — why we do NOT manipulate history by hand: TanStack Router patches
 * the History API (on the `window.history` instance) and, besides keeping an
 * index/key per entry, fires `router.load()` on every new entry. Pushing
 * a manual entry:
 *   - if it duplicates the router index → `delta = 0` on popstate → loop → HANG;
 *   - if it increments it → the router treats it as real navigation, reloads the route, and
 *     REMOUNTS the component, resetting the overlay's local signal → the overlay DOES NOT OPEN.
 * That is why we use the router's own mechanism: `useBlocker`. We do not touch
 * history when opening (the overlay opens normally); we only intercept the "BACK" action:
 * we close the topmost overlay and cancel navigation (the router does `go(1)`).
 *
 * LIFO: multiple nested overlays register their entry; "back" closes the most recent
 * one still open. It does not interfere with real navigation (PUSH/REPLACE/FORWARD pass).
 */

interface OverlayEntry {
  isOpen: () => boolean;
  close: () => void;
}

const stack: OverlayEntry[] = [];

/**
 * Pure, testable logic: given a navigation action, decides whether it should CLOSE the
 * overlay (and return `true` to BLOCK navigation) instead of navigating.
 * It only intercepts "BACK", and only if `entry` is the most recent open overlay.
 */
export function resolveBackDismiss(
  action: string,
  entry: OverlayEntry,
  overlays: readonly OverlayEntry[] = stack,
): boolean {
  if (action !== "BACK") {
    return false;
  }
  const topmostOpen = overlays.findLast((candidate) => candidate.isOpen());
  if (topmostOpen === entry) {
    entry.close();
    return true;
  }
  return false;
}

/**
 * @param isOpen  reactive signal: `true` while the overlay is open.
 * @param close   closes the overlay (e.g. `() => onOpenChange(false)`).
 */
export function useBackDismiss(isOpen: () => boolean, close: () => void): void {
  // Without a RouterProvider (isolated component tests / SSR) the blocker does
  // not apply; back-dismiss is progressive enhancement, so we no-op. In
  // production the overlay is always mounted inside the router.
  if (!useRouter({ warn: false })) {
    return;
  }

  // CC-19: closing is DEFERRED to a macrotask so it runs AFTER the
  // `history.go(1)` that the library executes when blocking BACK. That way a `close()`
  // that navigates (URL-addressable overlays toggling a search param with
  // `replace:true`) does not compete with that restoration and leaves URL/state
  // synchronized (overlay does not bounce open upon reload/re-navigating). For
  // local signal overlays the deferral is imperceptible.
  const deferredClose = () => {
    setTimeout(close, 0);
  };
  const entry: OverlayEntry = { isOpen, close: deferredClose };
  stack.push(entry);
  onCleanup(() => {
    const idx = stack.indexOf(entry);
    if (idx !== -1) {
      stack.splice(idx, 1);
    }
  });

  // `enableBeforeUnload: false` → we do not want the native "leave site?" prompt.
  useBlocker({
    enableBeforeUnload: false,
    shouldBlockFn: ({ action }) => resolveBackDismiss(action, entry),
  });
}
